import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon, ExportIcon } from 'components/icons';
import { Tooltip } from 'components/ui';
import { PAID_PLAN_TOOLTIP, subscriptionAccess } from 'config.js';
import querystring from 'querystring';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppSelector } from 'store';
import { composableFiltersState } from 'store/composableFiltersSlice';
import {
  useExportCustomersListMutation,
  useGetCustomerStatisticsQuery,
} from 'store/services/segmentsApi';
import { userDetailsState } from 'store/userDetailsSlice';
import { useParseURLQueryParams } from 'utils';
import { useStoreSubscriptionPlan } from 'utils/useStoreSubscriptionPlan';

export default function ExportUsers() {
  const { query } = useParseURLQueryParams();
  const location = useLocation();

  const [exportUsersToFile, { isLoading: fileDownloading }] =
    useExportCustomersListMutation();

  const { output, applied } = useAppSelector(composableFiltersState);
  const { currentStore } = useAppSelector(userDetailsState);

  const currentSegment = applied.segment;

  const { data } = useGetCustomerStatisticsQuery(
    {
      body: output.filtersApiPayload,
      params: {
        metric_list: ['order_frequency'],
        store_name: String(currentStore.completeInfo?.store_name),
      },
    },
    { skip: !!!currentStore.completeInfo?.store_name }
  );

  const segmentSizeWarningSize = 50000;

  const segmentSize = data?.order_frequency
    ? Object.values(data.order_frequency).reduce((a, b) => a + b, 0)
    : 0;

  const handleExport = (/** @type {string} */ content) => {
    if (segmentSize > segmentSizeWarningSize) {
      toast.info(
        'The segment you are exporting contains over 50,000 users. We are working on your request',
        { autoClose: 15000 }
      );
    }
    const queryParams =
      query.store_name &&
      querystring.stringify({
        store_name: query.store_name,
        export_format: 'csv',
        export_content: content,
      });

    exportUsersToFile({
      serializedParams: queryParams,
      body: currentSegment?.json_filter
        ? currentSegment?.json_filter
        : output.filtersApiPayload,
      provider: content === 'tresl' ? 'csv' : content,
      ...(currentSegment?.segment_name && {
        segmentName: currentSegment.segment_name,
      }),
    });
  };

  const { storeSubscriptionPlan } = useStoreSubscriptionPlan();

  return (
    <Tooltip
      text={PAID_PLAN_TOOLTIP}
      placement="top"
      disabled={!!!(storeSubscriptionPlan < subscriptionAccess.exportUsersMenu)}
    >
      <div className="relative">
        <Menu>
          {({ open }) => (
            <>
              <Menu.Button
                className="inline-flex items-center justify-center h-12 space-x-2 font-bold border w-44 rounded-2xl border-segments-accent-color-1 text-segments-accent-color-1 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={fileDownloading || !!!segmentSize}
              >
                {fileDownloading ? (
                  <LoadingSpinner />
                ) : (
                  <>
                    <ExportIcon className="w-5 h-5 text-segments-accent-color-1" />
                    <p className="">Export users</p>
                    <ChevronDownIcon className="w-5 h-5" />
                  </>
                )}
              </Menu.Button>

              <Transition
                show={open}
                enter="transition ease-out duration-100 "
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items
                  static
                  className="absolute right-0 z-50 w-full mt-1 origin-bottom-right bg-white border border-gray-200 divide-y divide-gray-100 rounded-md shadow-lg outline-none"
                >
                  <div className="py-1">
                    <Menu.Item
                      as="button"
                      className={`flex justify-between w-full px-4 py-2 text-sm leading-5 text-left text-gray-700 hover:bg-segments-gray-F1 ${
                        storeSubscriptionPlan <
                          subscriptionAccess.exportUsersMenu && 'opacity-50'
                      }`}
                      onClick={() => handleExport('tresl')}
                      disabled={
                        storeSubscriptionPlan <
                        subscriptionAccess.exportUsersMenu
                      }
                    >
                      CSV (SpreadSheet)
                    </Menu.Item>
                    <Menu.Item
                      as="button"
                      className={`flex justify-between w-full px-4 py-2 text-sm leading-5 text-left text-gray-700 hover:bg-segments-gray-F1 ${
                        storeSubscriptionPlan <
                          subscriptionAccess.exportUsersMenu && 'opacity-50'
                      }`}
                      onClick={() => handleExport('facebook')}
                      disabled={
                        storeSubscriptionPlan <
                        subscriptionAccess.exportUsersMenu
                      }
                    >
                      Facebook Audience
                    </Menu.Item>
                    <Menu.Item
                      as="button"
                      className={`flex justify-between w-full px-4 py-2 text-sm leading-5 text-left text-gray-700 hover:bg-segments-gray-F1 ${
                        storeSubscriptionPlan <
                          subscriptionAccess.exportUsersMenu && 'opacity-50'
                      }`}
                      onClick={() => handleExport('google')}
                      disabled={
                        storeSubscriptionPlan <
                        subscriptionAccess.exportUsersMenu
                      }
                    >
                      Google Audience
                    </Menu.Item>
                    <Menu.Item
                      as="button"
                      className={`flex justify-between w-full px-4 py-2 text-sm leading-5 text-left text-gray-700 hover:bg-segments-gray-F1 ${
                        storeSubscriptionPlan <
                          subscriptionAccess.exportUsersMenu && 'opacity-50'
                      }`}
                      onClick={() => handleExport('address')}
                      disabled={
                        storeSubscriptionPlan <
                        subscriptionAccess.exportUsersMenu
                      }
                    >
                      Addresses
                    </Menu.Item>
                  </div>

                  <div className="py-1">
                    <Menu.Item
                      to={{
                        pathname: '/customers/segments/integrations',
                        search: querystring.stringify({
                          ...query,
                          segment_name: currentSegment?.segment_name,
                        }),
                        state: { background: location },
                      }}
                      as={Link}
                      className={`flex justify-between w-full px-4 py-2 text-sm leading-5 text-left text-gray-800  focus:outline-none hover:bg-segments-gray-F1 ${
                        (!!!currentSegment ||
                          storeSubscriptionPlan <
                            subscriptionAccess.exportUsersMenu) &&
                        'cursor-not-allowed opacity-50'
                      }`}
                      title={
                        !!!currentSegment
                          ? 'Select a segment or filter first'
                          : ''
                      }
                    >
                      Sync segment
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </>
          )}
        </Menu>
      </div>
    </Tooltip>
  );
}

function LoadingSpinner() {
  return (
    <svg
      className="w-6 h-6 text-segments-accent-color-1 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );
}
